import { useEffect, useMemo, useState } from 'react';
import { Formik, Form, Field, FieldArray, ErrorMessage, useFormikContext, getIn } from 'formik';
import { KeenIcon, DefaultTooltip, LazyFormikSelect, DatePicker, CustomOption, CustomSingleValue } from '@/components';
import { useContactOptions, useLazyOptions } from '@/hooks';
import contactService from '@/services/contact/contactService';
import matterService from '@/services/matter/matterService';
import templateService from '@/services/billing/invoice/invoiceTemplateService';
import { toast } from 'sonner';
import { InputSanitizers } from '@/utils';
import invoiceService from '@/services/billing/invoice/invoiceService';
import { Skeleton } from '@/components/ui/skeleton'
import { useParams } from 'react-router';
import { sortData } from '@/utils';

const Setup = ({ invoiceId, templateData, contactId, matterId, canFetchTemplateData }) => {
    const { values, setFieldValue } = useFormikContext();
    const [dataLoading, setDataLoading] = useState(false);
    const [matterData, setMatterData] = useState([])
    const [templateSettings, setTemplateSettings] = useState(null)
    const [singleTemplateLoading, setSingleTemplateLoading] = useState(true)
    const { action, id } = useParams();
    const isEdit = action === 'edit';
    // const invoiceSettings = values.template_Settings?.invoice 
    const invoiceSettings = useMemo(() => {
        return values.template_Settings?.invoice;
    }, [values.template_Settings]);


    useEffect(() => {
        console.log('invoiceSettings', invoiceSettings)
    }, [invoiceSettings])

    const getSingleContact = async (contactId) => {
        setDataLoading(true)
        try {
            const { data, status } = await contactService.getSingleContact(contactId);
            if (status) {
                const Data = {
                    label: `${data.first_name + ' ' + data.last_name}`,
                    value: data.id.toString(),
                    id: data.id.toString(),
                    email: data.email,
                    business_name: data.business_name,
                    fullContact: data
                };
                console.log('Data', Data)
                return Data
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Contact.', {
                description: 'An error occurred while fetching contact. Please try again later.',
                action: {
                    label: 'Ok',
                },
            });
        } finally {
            setDataLoading(false)
        }
    }

    const loadContactOptions = useContactOptions({ defaultPageSize: 20 });

    const getAllMattersRelatedToContact = async (contactId, index) => {
        console.log('id', contactId)
        setDataLoading(true)
        try {
            const { data, status } = await invoiceService.getContactMatterRelatedData('contact', contactId);
            if (status) {
                return data.matters.map(m => ({
                    label: m.name,
                    value: m.id.toString(),
                    id: m.id.toString(),
                }));
            }
            return [];
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Matters.', {
                description: 'An error occurred while fetching matters. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const {
        data: matterOptions,
        loading: matterLoading,
        load: loadMatters,
        reset: resetMatters,
    } = useLazyOptions(async (contactId) => {
        if (!contactId) {
            return
        }
        return await getAllMattersRelatedToContact(contactId);
    });

    const getAllTemplates = async () => {
        try {
            const { data, status } = await templateService.getAllInvoiceTemplate();
            if (status) {
                const Data = sortData(data, { mode: "reverse" }).map(template => ({
                    label: template.name,
                    value: template.id.toString(),
                    id: template.id.toString(),
                }));
                setMatterData(Data)
                if (!values.invoice_template && !values.invoice_template_id) {
                    const defaultTemplate = (data || []).find(item => item.is_default === 1);
                    setFieldValue('invoice_template', {
                        label: defaultTemplate.name,
                        value: defaultTemplate.id,
                        id: defaultTemplate.id,
                    });
                    setFieldValue('invoice_template_id', defaultTemplate.id);
                    getSingleTemplate(defaultTemplate.id)
                }
                return Data
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Templates.', {
                description: 'An error occurred while fetching templates. Please try again later.',
            });
        }
    }
    const {
        data: templateOptions,
        loading: templateLoading,
        load: loadTemplates
    } = useLazyOptions(getAllTemplates);

    const getSingleTemplate = async (id) => {
        // setSingleTemplateLoading(true)
        try {
            const { data, status } = await templateService.getSingleInvoiceTemplate(id);
            if (status) {
                console.log('temp data', data)
                setTemplateSettings(data.settings.settings)
                setFieldValue('template_Settings', data.settings.settings)
                if (!canFetchTemplateData) {
                    setFieldValue('terms_and_conditions_note', data.settings.settings.invoice.notes)
                    setFieldValue('terms_and_conditions', data.settings.settings.invoice.terms_and_conditions)
                }
                // setFieldValue
            }
            console.log('data', data)
        } catch (error) {
            console.error('Error', error)
        } finally {
            setSingleTemplateLoading(false)
        }
    }

    // useEffect(() => {

    //   if(!invoiceId) {
    //     getAllTemplates()
    //   } else {
    //     if(!templateData) return
    //     getSingleTemplate(templateData?.invoice_template_id)
    //   }
    // }, [templateData])

    useEffect(() => {
        if (invoiceId && templateData) {
            getSingleTemplate(templateData?.invoice_template?.id);
        } else if (contactId || matterId) {
            const cid = contactId ? String(contactId) : '';
            const mid = matterId ? String(matterId) : '';

            setFieldValue('contact_id', cid);
            getSingleContact(cid).then((contactOption) => {
                console.log('CONTACT OPT', contactOption)
                setFieldValue('contact', contactOption);
                setFieldValue('invoice_business_name', contactOption.business_name);
                loadMatters(cid);

                const t = contactOption?.fullContact?.invoice_template;
                if (t) {
                    setFieldValue('invoice_template', { label: t.name, value: String(t.id), id: String(t.id) });
                    setFieldValue('invoice_template_id', String(t.id));
                    getSingleTemplate(t.id);
                }
            });

            setFieldValue('matter_id', mid); // keep for initial preselect
        } else if (!invoiceId) {
            getAllTemplates();
        }
    }, [invoiceId, templateData, contactId, matterId]);

    return (
        <>
            <div className='flex items-center my-5'>
                <span className='w-1/2 h-0.5 border border-dashed inline-block'></span>
                <p className='px-4 text-sm'>Setup</p>
                <span className='w-1/2 h-0.5 border border-dashed inline-block'></span>
            </div>
            <div className='flex flex-wrap items-start gap-5 sm:flex-nowrap'>
                <div className='w-full sm:w-1/2'>
                    <div className='flex items-center'>
                        <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                        <p className='text-sm'>Related To</p>
                    </div>
                    <div className="mt-5 space-y-5">
                        <div>
                            <label className="mb-1 form-label">Contact <span className='pl-px text-red-600'>*</span></label>
                            <LazyFormikSelect
                                name="contact_id"
                                placeholder="Select Contact"
                                disabled={contactId}
                                loadOptions={loadContactOptions}
                                pageSize={20}
                                preloadedOption={values.contact}
                                onOptionSelect={(selected) => {
                                    console.log('selected', selected)
                                    const cid = selected?.id ? String(selected.id) : '';
                                    setFieldValue('contact_id', cid);

                                    setFieldValue('invoice_business_name', selected?.business_name === null ? '' : selected?.business_name);
                                    resetMatters();
                                    setFieldValue('matter_id', '');
                                    setFieldValue('matter', null);

                                    if (cid) loadMatters(cid);

                                    const t = selected?.fullContact?.invoice_template;
                                    if (t) {
                                        setFieldValue('invoice_template', { label: t.name, value: String(t.id), id: String(t.id) });
                                        setFieldValue('invoice_template_id', String(t.id));
                                        getSingleTemplate(t.id);
                                    } else {
                                        setFieldValue('invoice_template', null);
                                        setFieldValue('invoice_template_id', '');
                                    }
                                }}
                                components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
                            />
                            <ErrorMessage
                                name={`contact_id`}
                                component="div"
                                className="mt-1 text-xs text-red-500"
                            />
                        </div>
                        {values.contact_id && (
                            <div>
                                <label className="mb-1 form-label">Contact Business Name</label>
                                <Field className="input" type="text" name={`invoice_business_name`} />
                                <ErrorMessage name={`invoice_business_name`} component="div" className="mt-1 text-xs text-red-500" />
                            </div>
                        )}
                        <div>
                            <label className="mb-1 form-label">Matter</label>
                            <LazyFormikSelect
                                key={`${getIn(values, 'contact_id') || 'no-contact'}`}
                                name="matter_id"
                                options={matterOptions}
                                placeholder="Select Matter"
                                onFocus={() => {
                                    const cid = getIn(values, 'contact_id');
                                    loadMatters(cid || null);
                                }}
                                isLoading={matterLoading}
                                preloadedOption={values.matter}
                            />
                            <ErrorMessage name={`matter_id`} component="div" className="mt-1 text-xs text-red-500" />
                        </div>
                        <div>
                            <label className="mb-1 form-label">Template <span className='pl-px text-red-600'>*</span></label>
                            <LazyFormikSelect
                                name={`invoice_template_id`}
                                options={templateOptions}
                                placeholder="Select Template"
                                onFocus={() => loadTemplates(null)}
                                isLoading={templateLoading}
                                preloadedOption={values.invoice_template}
                                // customClassNames={{
                                //     menuList: '!max-h-[120px]',
                                // }}
                                onOptionSelect={(selected) => { // This will be called when an option is selected
                                    console.log('selected', selected.id)
                                    if (selected?.id) getSingleTemplate(selected.id);
                                }}
                            />
                            <ErrorMessage name={`invoice_template_id`} component="div" className="mt-1 text-xs text-red-500" />
                        </div>
                        {/* <div>
                        <label className="mb-1 form-label">Template <span className='pl-px text-red-600'>*</span></label>
                        <LazyFormikSelect
                            name={`category_id`}
                            options={categoryOptions}
                            placeholder="Select Matter"
                            onFocus={loadCategories}
                            isLoading={categoryLoading}
                            noOptionsMessage={() => (
                                isLoading ? (
                                    <span className="text-sm">Loading...</span>
                                ) : (
                                    <div>
                                        <span className="mr-1 text-sm">No options available</span>
                                        <Can I='create' a='category' ability={ability}>
                                            <button type="button"
                                                onClick={() => setOpen(true)}
                                                className="text-sm text-blue-500 underline"
                                            >
                                                Add new
                                            </button>
                                        </Can>
                                    </div>
                                )
                            )}
                            preloadedOption={initialValues.category}
                        />
                        <ErrorMessage name={`category_id`} component="div" className="mt-1 text-xs text-red-500" />
                    </div> */}
                        <Field name="is_approval_required">
                            {({ field, form }) => (
                                <label className="switch w-fit">
                                    <span className="switch-label">Submit this Invoice For Approval?</span>
                                    <input
                                        // disabled={isEdit}
                                        type="checkbox"
                                        checked={field.value}
                                        onChange={() => form.setFieldValue("is_approval_required", !field.value)}
                                    />
                                </label>
                            )}
                        </Field>
                    </div>
                </div>
                <div className='w-full sm:w-1/2'>
                    <div className='flex items-center'>
                        <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                        <p className='text-sm'>Invoice</p>
                    </div>

                    <div className="mt-5 space-y-5">
                        {/* {invoiceSettings?.show_invoice_number && (
                        <div>
                            <DefaultTooltip title='Choose any number for this invoice. When you create your next invoice, it will automatically be incremented to be the next highest invoice number..' placement="top" className="max-w-full">
                                <label className="inline-flex items-center gap-1 mb-1 form-label w-fit">
                                    <KeenIcon icon="information" />
                                    {invoiceSettings?.invoice_number_label || 'Number'}
                                </label>
                            </DefaultTooltip>
                            <Field className="input" type="text" name={`number`} onInput={InputSanitizers} />
                        </div>
                    )} */}
                        {/* {singleTemplateLoading ? (
                        <>
                        <Skeleton className="w-[12%] h-[18px] !mt-5 !mb-1" />
                        <Skeleton className="w-full h-10 !mt-0" />
                        </>
                        ) : invoiceSettings?.show_invoice_number ? (
                        <div>
                            <DefaultTooltip
                            title="Choose any number for this invoice. When you create your next invoice, it will automatically be incremented to be the next highest invoice number."
                            placement="top"
                            className="max-w-full"
                            >
                            <label className="inline-flex items-center gap-1 mb-1 form-label w-fit">
                                <KeenIcon icon="information" />
                                {invoiceSettings?.invoice_number_label || 'Number'}
                            </label>
                            </DefaultTooltip>
                            <Field
                            className="input"
                            type="text"
                            name="number"
                            onInput={InputSanitizers}
                            />
                        </div>
                    ) : null} */}

                        {singleTemplateLoading ? (
                            <>
                                <Skeleton className="w-[12%] h-[18px] !mt-5 !mb-1" />
                                <Skeleton className="w-full h-10 !mt-0" />
                            </>
                        ) : invoiceSettings?.show_issue_date ? (
                            <div>
                                <label className="mb-1 form-label">
                                    {invoiceSettings?.issue_date_label || 'Date'} <span className='pl-px text-red-600'>*</span>
                                </label>
                                <Field name="date" component={DatePicker} isRange={false} />
                                <ErrorMessage name="date" component="div" className="mt-1 text-xs text-red-500" />
                            </div>
                        ) : null}

                        {singleTemplateLoading ? (
                            <>
                                <Skeleton className="w-[12%] h-[18px] !mt-5 !mb-1" />
                                <Skeleton className="w-full h-10 !mt-0" />
                            </>
                        ) : invoiceSettings?.show_due_date ? (
                            <div>
                                <label className="mb-1 form-label">
                                    {invoiceSettings?.due_date_label || 'Due Date'}
                                </label>
                                <Field name="due_date" component={DatePicker} isRange={false} />
                                <ErrorMessage name={`due_date`} component="div" className="mt-1 text-xs text-red-500" />
                            </div>
                        ) : null}
                        <div>
                            <label className="mb-1 form-label">Discount (%)</label>
                            <Field className="input" type="text" name={`discount_percent`} onInput={InputSanitizers} />
                            <ErrorMessage name="discount_percent" component="div" className="mt-1 text-xs text-red-500" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export { Setup }
